import React, { useEffect, useMemo, useState } from 'react';
import { Button, Input, Select, Table, Tag } from 'antd';
import { PlusOutlined, DeleteOutlined, ReloadOutlined } from '@ant-design/icons';
import AppShell from './components/AppShell';
import Panel from './components/Panel';
import TableSkeleton from './components/TableSkeleton';
import { Badge } from './components/StatusPill';
import Field from './components/Field';
import axiosInstance from './utils/axiosInstance';
import { money } from './utils/format';
import {
    NO_SET,
    ProductFilters,
    setsForProduct,
    filterProducts,
    sellerOptions,
    hasActiveFilters,
    parseSetItems,
} from './utils/productFilter';
import { confirmDialog, errorDialog } from './utils/dialog';
import { useNavigate } from 'react-router-dom';

const { Column } = Table;

interface ProductType {
    key: React.Key;
    id: number;
    name: string;
    seller: string;
    price: number;
    img: string;
}


const ProductPage: React.FC = () => {
    const [products, setProducts] = useState<ProductType[]>([]);
    const [productSets, setProductSets] = useState<any[]>([]);
    const [loading, setLoading] = useState(true);
    const [filters, setFilters] = useState<ProductFilters>({});
    const navigate = useNavigate();

    const getProducts = async () => {
        setLoading(true);
        try {
            const response = await axiosInstance.post("admin/protected/allProducts", {})
            const data = response?.data?.data;
            if (data) {
                console.log(data)
                setProducts(data.map((e: any) => {
                    return {
                        id: e.id,
                        key: e.id,
                        name: e.name,
                        price: e.price,
                        seller: e.seller,
                        img: e.img
                    } as ProductType;
                }));
            }
        } finally {
            setLoading(false);
        }
    }

    const deleteProduct = async (product: ProductType) => {
        if (!await confirmDialog({ title: '确定删除？', text: `删除 ${product.name}` })) return;
        try {
            await axiosInstance.post("admin/protected/deleteProduct", {
                id: product.id
            })
            getProducts();
        } catch (error: any) {
            errorDialog();
        }
    }


    useEffect(() => {
        getProducts();
        axiosInstance.post('admin/protected/getProductSets').then(res => {
            if (res.data?.data) {
                setProductSets(res.data.data);
            }
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    const filtered = useMemo(
        () => filterProducts(products, filters, productSets),
        [products, filters, productSets],
    );

    const sellers = useMemo(() => sellerOptions(products), [products]);

    const setField = (key: keyof ProductFilters) => (
        e: React.ChangeEvent<HTMLInputElement>,
    ) => setFilters((prev) => ({ ...prev, [key]: e.target.value }));

    const reset = () => setFilters({});

    return (
        <AppShell
            title="商品"
            subtitle={hasActiveFilters(filters)
                ? `${filtered.length} / ${products.length} 商品`
                : `${products.length} 商品`}
            actions={
                <Button icon={<PlusOutlined />} type="primary" onClick={() => navigate("/newproduct")}>新建</Button>
            }
        >
            <Panel className="mb-4">
                <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-4">
                    <Field label="名称">
                        <Input allowClear value={filters.name} placeholder="商品名称" onChange={setField('name')} />
                    </Field>
                    <Field label="卖家">
                        <Select
                            allowClear
                            value={filters.seller}
                            placeholder="全部卖家"
                            onChange={(v) => setFilters((prev) => ({ ...prev, seller: v }))}
                            options={sellers.map(s => ({ value: s, label: s }))}
                        />
                    </Field>
                    <Field label="最小价钱">
                        <Input allowClear type="number" value={filters.minPrice} placeholder="最小价钱" onChange={setField('minPrice')} />
                    </Field>
                    <Field label="最大价钱">
                        <Input allowClear type="number" value={filters.maxPrice} placeholder="最大价钱" onChange={setField('maxPrice')} />
                    </Field>
                    <Field label="配套">
                        <Select
                            allowClear
                            value={filters.set}
                            placeholder="全部配套"
                            onChange={(v) => setFilters((prev) => ({ ...prev, set: v }))}
                            options={[
                                ...productSets.map((s: any) => ({ value: String(s.id), label: s.name })),
                                { value: NO_SET, label: '无配套' },
                            ]}
                        />
                    </Field>
                </div>

                <div className="mt-4 flex gap-2">
                    <Button icon={<ReloadOutlined />} onClick={reset}>重置</Button>
                </div>
            </Panel>

            <Panel flush>
            {loading ? <TableSkeleton rows={5} columns={5} /> : (
            <Table<ProductType> dataSource={filtered} scroll={{ x: 'max-content' }} size="middle">
                <Column title="ID" dataIndex="id" key="id" width={70} />
                <Column
                    title="照片"
                    key="image"
                    width={110}
                    render={(_: any, record: ProductType) => (
                        <div className="group h-[72px] w-[72px] overflow-hidden rounded-lg border border-line
                                        shadow-soft transition-shadow duration-250 hover:shadow-lifted">
                            <img
                                alt={record.name}
                                src={`${process.env.REACT_APP_FOLDER_URL}/${record.img}`}
                                className="h-full w-full object-cover transition-transform duration-300
                                           ease-smooth group-hover:scale-110"
                            />
                        </div>
                    )}
                />
                <Column title="名称" key="name"
                    render={(_: any, record: ProductType) => (
                        <span className="font-semibold text-ink">{record.name}</span>
                    )}
                />
                <Column title="卖家" key="seller"
                    render={(_: any, record: ProductType) => (
                        <span className="text-muted">{record.seller}</span>
                    )}
                />
                <Column title="价钱" key="price" className='tabular'
                    render={(_: any, record: ProductType) => (
                        <span className="font-semibold text-ink">
                            <span className="mr-1 text-xs font-normal text-faint">RM</span>
                            {money(record.price)}
                        </span>
                    )}
                />
                <Column title="配套" key="sets"
                    render={(_: any, record: ProductType) => {
                        const matchedSets = setsForProduct(record, productSets);
                        return (
                            <div className="flex flex-wrap gap-1">
                                {matchedSets.map((s: any) => (
                                    <Tag color="blue" key={s.id}>{s.name}</Tag>
                                ))}
                                {matchedSets.length === 0 && <span className="text-muted text-xs">无配套</span>}
                            </div>
                        );
                    }}
                />
                <Column title="类型" key="type"
                    render={(_: any, record: ProductType) => {
                        const isPremium = setsForProduct(record, productSets).some(s => {
                            const match = parseSetItems(s.items).find(item => item.name === record.name);
                            return Boolean(match?.premium);
                        });
                        return isPremium
                            ? <Badge tone="caution">高级</Badge>
                            : <Badge tone="info">普通</Badge>;
                    }}
                />
                <Column
                    title="操作"
                    key="action"
                    fixed="right"
                    width={110}
                    render={(_: any, record: ProductType) => (
                        <Button size="small" danger icon={<DeleteOutlined />} onClick={() => deleteProduct(record)}>删除</Button>
                    )}
                />
            </Table>
            )}
            </Panel>
        </AppShell>
    );
}

export default ProductPage;